import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { assignmentsApi, getErrorMessage, getMediaUrl, markNotificationsForOpenContext, type Patch } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

function adminPatchLabel(patch: Patch) {
  if (patch.review_status !== 'saved') return 'Not reviewed';
  return patch.is_modified ? 'Reannotated' : 'Approved';
}

export function AdminReviewPage() {
  const { sampleId, assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [doctorName, setDoctorName] = useState('');
  const [doctorNote, setDoctorNote] = useState('');
  const [patches, setPatches] = useState<Patch[]>([]);

  const load = useCallback(async () => {
    if (!assignmentId) return;
    setLoading(true);
    try {
      const [{ data: review }, { data: patchList }] = await Promise.all([
        assignmentsApi.review(assignmentId),
        assignmentsApi.patches(assignmentId),
      ]);
      const r = review as { doctor_name: string; doctor_note: string };
      setDoctorName(r.doctor_name);
      setDoctorNote(r.doctor_note || '');
      setPatches(Array.isArray(patchList) ? patchList : []);
    } catch (err) {
      toast.error(getErrorMessage(err));
      navigate(`/admin/samples/${sampleId}`);
    } finally {
      setLoading(false);
    }
  }, [assignmentId, sampleId, navigate]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!assignmentId && !sampleId) return;
    void markNotificationsForOpenContext({
      ...(assignmentId ? { assignment_id: assignmentId } : {}),
      ...(!assignmentId && sampleId ? { sample_id: sampleId } : {}),
    });
  }, [assignmentId, sampleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={`Review — ${doctorName}`}
        subtitle="Read-only doctor annotations"
        backTo={`/admin/samples/${sampleId}`}
      />
      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {doctorNote && (
          <Card>
            <CardHeader><CardTitle className="text-base">Doctor note</CardTitle></CardHeader>
            <CardContent><p className="text-sm">{doctorNote}</p></CardContent>
          </Card>
        )}
        <Card>
          <CardHeader><CardTitle>Patches</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {patches.map((patch) => (
                <div key={patch.id} className="border rounded-lg overflow-hidden">
                  <img src={getMediaUrl(patch.image_path)} alt={patch.filename} className="w-full aspect-square object-cover" />
                  <div className="p-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-medium">{patch.patch_identifier}</span>
                    <Badge variant="secondary" className="text-[10px]">{adminPatchLabel(patch)}</Badge>
                  </div>
                  <div className="px-2 pb-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate(`/admin/samples/${sampleId}/reviews/${assignmentId}/patches/${patch.id}`)}
                    >
                      <Eye className="size-3 mr-1" /> View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
